import React, { createContext, useEffect, useState } from "react";
import { UserProfile } from "../Models/User";
import { useNavigate } from "react-router";
import { loginAPI, registerAPI } from "../Services/AuthService";
import { toast } from "react-toastify";
import axios from "axios";

type UserContextType = {
  user: UserProfile | null;
  token: string | null;
  registerUser: (email: string, username: string, password: string) => void;
  loginUser: (username: string, password: string, rememberMe?: boolean) => void;
  logOut: () => void;
  isLoggedIn: () => boolean;
};

type Props = {
  children: React.ReactNode;
};

const UserContext = createContext<UserContextType>({} as UserContextType);

const STORAGE_EXPIRY_KEY = "authExpiry";
const HALF_DAYS_MS = 1 * 12 * 60 * 60 * 1000;

export const UserProvider = ({ children }: Props) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    const expiryTime = localStorage.getItem(STORAGE_EXPIRY_KEY);
    const isExpired = expiryTime && Date.now() > parseInt(expiryTime);

    if (isExpired) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem(STORAGE_EXPIRY_KEY);
    }

    let storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    let storedToken = localStorage.getItem("token") || sessionStorage.getItem("token");

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    }
    setIsReady(true);
  }, []);

  const loginUser = async (username: string, password: string, rememberMe: boolean = false) => {
    await loginAPI(username, password)
      .then((response) => {
        if (response) {
          const storage = rememberMe ? localStorage : sessionStorage;
          storage.setItem("token", response?.data.token);
          const userObj = {
            userName: response?.data.userName,
            email: response?.data.email,
          };
          storage.setItem("user", JSON.stringify(userObj));

          if (rememberMe) {
            const expiryTime = Date.now() + HALF_DAYS_MS;
            localStorage.setItem(STORAGE_EXPIRY_KEY, expiryTime.toString());
          }

          setToken(response?.data.token);
          setUser(userObj);
          axios.defaults.headers.common["Authorization"] = `Bearer ${response?.data.token}`;
          toast.success("Login successful!");

          setTimeout(() => navigate("/search"), 100);
        }
      })
      .catch(() => toast.warning("Login failed!"));
  };

  const registerUser = async (email: string, username: string, password: string) => {
    await registerAPI(email, username, password)
      .then((response) => {
        if (response) {
          sessionStorage.setItem("token", response?.data.token);
          const userObj = {
            userName: response?.data.userName,
            email: response?.data.email,
          };
          sessionStorage.setItem("user", JSON.stringify(userObj));
          setToken(response?.data.token);
          setUser(userObj);
          axios.defaults.headers.common["Authorization"] = `Bearer ${response?.data.token}`;
          toast.success("Registration successful!");

          setTimeout(() => navigate("/search"), 100);
        }
      })
      .catch(() => toast.warning("Registration failed!"));
  };

  const isLoggedIn = () => {
    return !!user;
  };

  const logOut = () => {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem(STORAGE_EXPIRY_KEY);
    setUser(null);
    setToken(null);
    axios.defaults.headers.common["Authorization"] = "";
    toast.info("Logged out successfully!");
    navigate("/login");
  };

  return (
    <UserContext.Provider value={{ user, token, registerUser, loginUser, logOut, isLoggedIn }}>
      {isReady && children}
    </UserContext.Provider>
  );
};

export const useAuth = () => {
  return React.useContext(UserContext);
};
